import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Clock, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * iter102 — Trial countdown bar.
 *
 * Renders a thin orange strip above the main header during a Stripe-managed
 * Pro trial period. Shows "X days of free Pro left · Manage" so users always
 * know exactly when the £1.99 will hit, plus a one-tap link to the Stripe
 * Customer Portal so they can cancel before day 15 if they want to.
 *
 * Self-dismissible (LocalStorage) for the duration of the trial — re-renders
 * after a new login or trial change.
 *
 * Hidden when:
 *   - user is logged out
 *   - user isn't on a Stripe trial (no stripe_status === "trialing")
 *   - user has already dismissed it this trial
 *   - membership_expires_at is missing / in the past
 */
const STORAGE_KEY = "cc_trial_bar_dismissed_v1";

function daysBetween(future) {
  try {
    const ms = new Date(future).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
}

export default function TrialCountdownBar() {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/billing/me")
      .then(({ data }) => { if (alive) setBilling(data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  // Read dismissal tied to the specific trial end date — so a new trial
  // (e.g. user cancels then resubscribes) shows the bar again automatically.
  useEffect(() => {
    if (!billing?.membership_expires_at) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && stored === billing.membership_expires_at) setDismissed(true);
    } catch { /* SSR / private-mode */ }
  }, [billing?.membership_expires_at]);

  if (!user || !billing) return null;
  if (billing.stripe_status !== "trialing") return null;
  if (dismissed) return null;
  const left = daysBetween(billing.membership_expires_at);
  if (left == null) return null;

  const onDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, billing.membership_expires_at); } catch { /* private mode */ }
    setDismissed(true);
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error("Could not open billing portal. Try again in a sec.");
    } finally {
      setBusy(false);
    }
  };

  const label = left === 0
    ? "Last day of free Pro — first charge tomorrow"
    : left === 1
      ? "1 day of free Pro left"
      : `${left} days of free Pro left`;

  return (
    <div
      className="bg-[#ff4500] text-white text-[12px] mono"
      role="status"
      aria-live="polite"
      data-testid="trial-countdown-bar"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-1.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Crown className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
          <span className="font-bold uppercase tracking-wider truncate" data-testid="trial-countdown-label">
            {label}
          </span>
          <Clock className="w-3 h-3 hidden sm:inline shrink-0 opacity-80" strokeWidth={2} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={openPortal}
            disabled={busy}
            className="underline underline-offset-2 font-bold uppercase tracking-wider hover:text-white/80 disabled:opacity-60"
            data-testid="trial-countdown-manage"
          >
            {busy ? "Opening…" : "Manage / cancel"}
          </button>
          <Link
            to="/pro"
            className="hidden sm:inline underline underline-offset-2 hover:text-white/80"
            data-testid="trial-countdown-details"
          >
            Details
          </Link>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="opacity-80 hover:opacity-100"
            data-testid="trial-countdown-dismiss"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
